// @ts-nocheck
import { Component } from "@theme/component";
import { watchAny } from "@theme/utilities";

// 移动端检测阈值
const MOBILE_BREAKPOINT = 768;

class Select extends Component {
  // 静态属性：跨实例共享状态
  static currentOpenSelect = null;
  static sharedBottomSheet = null;
  static sharedOverlay = null;
  static activeSelectComponent = null;
  #isOpen;

  constructor() {
    super();

    this.#isOpen = watchAny(false, (newVal, oldVal) => {
      const isMobile = this.isMobile();

      if (newVal) {
        if (Select.currentOpenSelect && Select.currentOpenSelect !== this) {
          Select.currentOpenSelect.#isOpen.value = false;
        }
        Select.currentOpenSelect = this;

        if (isMobile) {
          // 移动端：只显示底部弹窗，不设置 open 属性
          this.showBottomSheet();
        } else {
          // PC端：只设置 open 属性显示下拉
          this.setAttribute("open", "");
        }
      } else {
        if (Select.currentOpenSelect === this) {
          Select.currentOpenSelect = null;
        }

        if (isMobile) {
          // 移动端：隐藏底部弹窗
          this.hideBottomSheet();
        } else {
          // PC端：移除 open 属性
          this.removeAttribute("open");
        }
      }
    });
  }

  // 检测是否是移动端
  isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  // 初始化共享的底部弹窗（仅创建一次）
  static initSharedBottomSheet() {
    if (Select.sharedOverlay) return;

    // 创建遮罩层
    Select.sharedOverlay = document.createElement("div");
    Select.sharedOverlay.className = "select-overlay";
    Select.sharedOverlay.addEventListener("click", () => {
      if (Select.activeSelectComponent) {
        Select.activeSelectComponent.#isOpen.value = false;
      }
    });

    // 创建底部弹窗容器
    Select.sharedBottomSheet = document.createElement("div");
    Select.sharedBottomSheet.className = "select-bottom-sheet";

    // 创建底部弹窗头部
    const header = document.createElement("div");
    header.className = "select-bottom-sheet__header";

    const title = document.createElement("span");
    title.className = "select-bottom-sheet__title";

    const closeBtn = document.createElement("button");
    closeBtn.className = "select-bottom-sheet__close";
    closeBtn.type = "button";
    closeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
    closeBtn.addEventListener("click", () => {
      if (Select.activeSelectComponent) {
        Select.activeSelectComponent.#isOpen.value = false;
      }
    });

    header.appendChild(title);
    header.appendChild(closeBtn);

    // 创建选项列表容器
    const content = document.createElement("div");
    content.className = "select-bottom-sheet__content";

    // 使用事件委托处理点击（性能优化：避免多个事件监听器）
    content.addEventListener("click", (e) => {
      const item = e.target.closest("select-item");
      if (item && Select.activeSelectComponent) {
        e.preventDefault();
        e.stopPropagation();
        const value = item.getAttribute("value");
        const originalItem = Select.activeSelectComponent.querySelector(`select-item[value="${value}"]`);
        if (originalItem) {
          Select.activeSelectComponent.selectItem(originalItem, true, true);
        }
      }
    });

    Select.sharedBottomSheet.appendChild(header);
    Select.sharedBottomSheet.appendChild(content);

    // 添加到 body
    document.body.appendChild(Select.sharedOverlay);
    document.body.appendChild(Select.sharedBottomSheet);
  }

  // 更新底部弹窗内容
  updateBottomSheetContent() {
    if (!Select.sharedBottomSheet) return;

    const title = Select.sharedBottomSheet.querySelector(".select-bottom-sheet__title");
    const content = Select.sharedBottomSheet.querySelector(".select-bottom-sheet__content");

    // 更新标题
    title.textContent = this.getAttribute("data-title") || Theme.translations.select_label || "Select";

    // 清空并重新填充内容
    content.innerHTML = "";

    // 使用 DocumentFragment 批量插入（性能优化）
    const fragment = document.createDocumentFragment();
    const items = this.querySelectorAll("select-item");

    items.forEach((item) => {
      const clonedItem = item.cloneNode(true);
      fragment.appendChild(clonedItem);
    });

    content.appendChild(fragment);
  }

  // 显示底部弹窗
  showBottomSheet() {
    Select.initSharedBottomSheet();
    Select.activeSelectComponent = this;
    this.updateBottomSheetContent();

    // 锁定背景滚动
    document.documentElement.setAttribute("scroll-lock", "");

    // 触发动画
    requestAnimationFrame(() => {
      Select.sharedOverlay.classList.add("active");
      Select.sharedBottomSheet.classList.add("active");
    });
  }

  // 隐藏底部弹窗
  hideBottomSheet() {
    if (!Select.sharedBottomSheet || !Select.sharedOverlay) return;

    Select.sharedOverlay.classList.remove("active");
    Select.sharedBottomSheet.classList.remove("active");
    Select.activeSelectComponent = null;
    document.documentElement.removeAttribute("scroll-lock");
  }

  connectedCallback() {
    super.connectedCallback();

    // 初始化默认选择
    this.initializeDefaultSelection();

    // 点击组件内部的处理
    this.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      // 移动端时，点击选项不在这里处理（由底部弹窗处理）
      if (this.isMobile()) {
        if (!event.target.closest("select-item")) {
          this.#isOpen.value = !this.#isOpen.value;
        }
        return;
      }

      if (event.target.closest("select-item")) {
        const selectItem = event.target.closest("select-item");
        this.selectItem(selectItem, true, true);
        return;
      }

      this.#isOpen.value = !this.#isOpen.value;
    });

    // 点击页面其他地方时收起下拉菜单（仅PC端需要）
    this.handleOutsideClick = (event) => {
      // 移动端由遮罩层处理，跳过
      if (this.isMobile()) return;

      if (!this.contains(event.target)) {
        const clickedSelect = event.target.closest("select-component");
        if (!clickedSelect || clickedSelect !== this) {
          this.#isOpen.value = false;
        }
      }
    };

    document.addEventListener("click", this.handleOutsideClick);
  }

  // 初始化默认选择
  initializeDefaultSelection() {
    // 优先找有 selected 属性的，否则找第一个选项
    const targetItem = this.querySelector("select-item[selected]") || this.querySelector("select-item");
    if (targetItem) {
      // false, false 表示不关闭下拉菜单且不触发回调
      this.selectItem(targetItem, false, false);
    }
  }

  // 选择项目的方法
  selectItem(selectItem, closeDropdown = true, triggerCallback = true) {
    if (!selectItem) return;

    // 移除所有选项的选中状态
    this.querySelectorAll("select-item").forEach((item) => {
      item.removeAttribute("selected");
      item.classList.remove("selected");
    });

    // 设置当前选项为选中状态
    selectItem.setAttribute("selected", "");
    selectItem.classList.add("selected");

    // 更新组件的值
    this.value =
      selectItem.getAttribute("value") || selectItem.textContent.trim();

    // 更新显示文本（优先使用 data-select-label 属性标记的元素，回退到第一个子元素）
    const headerText = this.querySelector("select-header [data-select-label]") || this.querySelector("select-header").firstElementChild;
    if (headerText) {
      headerText.textContent = selectItem.textContent.trim();
    }

    // 准备回调数据
    const callbackData = {
      value: this.value,
      selectedItem: selectItem,
      component: this,
      data: {
        url: selectItem.getAttribute("data-url"),
        price: selectItem.getAttribute("data-price"),
        comparePrice: selectItem.getAttribute("data-compare-price"),
        discountPercentage: selectItem.getAttribute("data-discount-percentage"),
        image: selectItem.getAttribute("data-image"),
        variantId: selectItem.getAttribute("data-variant-id"),
      },
    };

    // 只有在需要触发回调时才执行
    if (triggerCallback) {
      // 获取回调函数
      const callbackName = this.getAttribute("data-callback");
      const callback = this.getCallbackFunction(callbackName);

      // 执行自定义回调函数
      if (callback && typeof callback === "function") {
        try {
          callback(callbackData);
        } catch (error) {
          console.error("Select callback error:", error);
        }
      }

      // 触发自定义事件
      this.dispatchEvent(
        new CustomEvent("select-change", {
          detail: callbackData,
        })
      );
    }

    // 关闭下拉菜单
    if (closeDropdown) {
      this.#isOpen.value = false;
    }
  }

  // 获取回调函数
  getCallbackFunction(callbackName) {
    if (!callbackName) return null;

    // 尝试从全局对象中获取回调函数
    if (window[callbackName] && typeof window[callbackName] === "function") {
      return window[callbackName];
    }

    // 尝试从组件的数据属性中获取回调函数
    const callbackData = this.getAttribute("data-callback-data");
    if (callbackData) {
      try {
        const callbackObj = JSON.parse(callbackData);
        if (
          callbackObj[callbackName] &&
          typeof callbackObj[callbackName] === "function"
        ) {
          return callbackObj[callbackName];
        }
      } catch (error) {
        console.error("Error parsing callback data:", error);
      }
    }

    return null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // 移除全局点击事件监听器
    if (this.handleOutsideClick) {
      document.removeEventListener("click", this.handleOutsideClick);
    }
  }
}

if (!customElements.get("select-component")) {
  customElements.define("select-component", Select);
}
